import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  linkedSignal,
  model,
  OnInit,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { TranslocoPipe } from '@jsverse/transloco';
import { filter, map } from 'rxjs';
import { NavbarBannerLogoComponent } from '../../../core/common/components/navbar-banner-logo/navbar-banner-logo.component';
import { SideMenuProfileComponent } from '../../../core/common/components/side-menu-profile/side-menu-profile.component';
import { AuthService } from '../../../core/common/services/auth/auth.service';
import { AvatarComponent } from '../avatar/avatar.component';
import { NotificationBellComponent } from '../notification-bell/notification-bell.component';
import { MenuItem, SidenavWidthBreakpoint } from '../sidenav/sidenav.model';
import { SideMenuGroupComponent } from './side-menu-group/side-menu-group.component';
import { SideMenuIconComponent } from './side-menu-icon/side-menu-icon.component';
import { SideMenuItemComponent } from './side-menu-item/side-menu-item.component';

interface SideMenuRow {
  item: MenuItem;
  key: string;
  group: boolean;
}

interface SideMenuBlock {
  rows: Array<SideMenuRow & { open: boolean }>;
  /** A rule above this block. */
  rule: boolean;
  /**
   * The rule separates two groups. Its vertical margins sum to 1.25× the
   * mt-md gap that used to sit between same-kind sections.
   */
  groupRule: boolean;
  /** Space above, with no rule: the previous section is pages as well. */
  spaced: boolean;
}

const groupKey = (item: MenuItem) => item.id ?? item.label;
const isGroup = (item: MenuItem) => !!item.children?.length;
const matchesUrl = (path: string | undefined, url: string) => {
  if (!path) {
    return false;
  }
  const current = url.split(/[?#]/)[0];
  return current === path || current.startsWith(path + '/');
};

@Component({
  selector: 'lib-side-menu',
  imports: [
    TranslocoPipe,
    NavbarBannerLogoComponent,
    AvatarComponent,
    NotificationBellComponent,
    SideMenuProfileComponent,
    SideMenuItemComponent,
    SideMenuGroupComponent,
    SideMenuIconComponent,
  ],
  templateUrl: './side-menu.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '(window:resize)': 'collapseOnNarrowScreen()',
    class: 'flex h-full min-h-0 flex-col',
  },
})
export class SideMenuComponent implements OnInit {
  private readonly router = inject(Router);
  private readonly authService = inject(AuthService);

  readonly sections = input.required<MenuItem[][]>();
  readonly variant = input<'employee' | 'admin'>('employee');
  readonly collapsed = model<boolean>(false);

  private readonly url = toSignal(
    this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      map(event => event.urlAfterRedirects)
    ),
    { initialValue: this.router.url }
  );

  /** Groups need children, pages need a path; anything else (e.g. a group emptied by filters) is dropped. */
  protected readonly visibleSections = computed<SideMenuRow[][]>(() =>
    this.sections()
      .map(section =>
        section
          .map(item => ({ item, key: groupKey(item), group: isGroup(item) }))
          .filter(row => row.group || !!row.item.path)
      )
      .filter(section => section.length > 0)
  );
  protected readonly blocks = computed<SideMenuBlock[]>(() => {
    const openKeys = new Set(this.openGroupKeys());
    const sections = this.visibleSections();
    const kinds = sections.map(rows => rows.every(row => row.group));
    return sections.map((rows, i) => {
      const groupRule = i > 0 && kinds[i] && kinds[i - 1];
      const kindChange = i > 0 && kinds[i] !== kinds[i - 1];
      const ruleBefore = i > 0 && !!rows[0]?.item.ruleBefore;
      return {
        rows: rows.map(row => ({ ...row, open: openKeys.has(row.key) })),
        rule: kindChange || groupRule || ruleBefore,
        groupRule,
        spaced: i > 0 && !kindChange && !groupRule && !ruleBefore,
      };
    });
  });
  private readonly activeGroupKey = computed(() => {
    const url = this.url() ?? '';
    const row = this.visibleSections()
      .flat()
      .find(
        r =>
          r.group &&
          !!r.item.children?.some(child => matchesUrl(child.path, url))
      );
    return row?.key ?? null;
  });
  /**
   * The first time groups arrive, any group marked `expanded` starts open
   * (the employee menu marks every group). Navigation then opens the active
   * page's group. Groups the user opened or closed stay that way, including
   * on a page outside every group.
   */
  protected readonly openGroupKeys = linkedSignal({
    source: computed(() => ({
      url: this.url(),
      sections: this.visibleSections(),
    })),
    computation: (source, previous): string[] => {
      const groups = source.sections.flat().filter(row => row.group);
      const valid = new Set(groups.map(row => row.key));
      const previousHadGroups = (previous?.source.sections ?? [])
        .flat()
        .some(row => row.group);
      const active = this.activeGroupKey();
      if (!previousHadGroups) {
        const initial = groups
          .filter(row => row.item.expanded)
          .map(row => row.key);
        if (active && !initial.includes(active)) {
          initial.push(active);
        }
        return initial;
      }
      const kept = (previous?.value ?? []).filter(key => valid.has(key));
      return active && !kept.includes(active) ? [...kept, active] : kept;
    },
  });

  protected readonly workspace = computed(() =>
    this.authService.selectedWorkspace()
  );
  protected readonly workspaceName = computed(
    () => this.workspace()?.name ?? this.workspace()?.slug ?? ''
  );
  protected readonly workspaceLogo = computed(
    () => this.workspace()?.images?.logo?.url ?? ''
  );
  protected readonly asideClass = computed(
    () =>
      'relative flex h-full min-h-0 shrink-0 flex-col border-e border-neutral-200 bg-white transition-[width] duration-300 ' +
      (this.collapsed() ? 'w-20' : 'w-66')
  );

  private wasNarrow = false;

  ngOnInit(): void {
    this.wasNarrow = window.innerWidth < SidenavWidthBreakpoint;
    if (this.wasNarrow) {
      this.collapsed.set(true);
    }
  }

  /** Collapses only when the window crosses below the breakpoint, so a manual expand sticks while narrow. */
  collapseOnNarrowScreen(): void {
    const narrow = window.innerWidth < SidenavWidthBreakpoint;
    if (narrow && !this.wasNarrow) {
      this.collapsed.set(true);
    }
    this.wasNarrow = narrow;
  }

  toggleCollapsed(): void {
    this.collapsed.update(value => !value);
  }

  toggleGroup(key: string): void {
    this.openGroupKeys.update(keys =>
      keys.includes(key) ? keys.filter(open => open !== key) : [...keys, key]
    );
  }
}
